import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Cake, ArrowRight, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);

  useEffect(() => {
    if (justRegistered) return;
    if (user && user.user_id) navigate("/app");
  }, [user, navigate, justRegistered]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    const r = await register(email, password, name, phone);
    setBusy(false);
    if (!r.ok) setErr(r.error);
    else {
      setJustRegistered(true);
      // Drop the user straight onto the Dashboard. The empty-state there
      // already prompts them to create their first group, so there's no
      // need for an interstitial profile/welcome step.
      navigate("/app", { replace: true });
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 font-extrabold text-xl mb-8 justify-center" data-testid="register-logo">
          <span className="w-9 h-9 grid place-items-center" style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: "var(--radius-btn)" }}>
            <Cake size={18} />
          </span>
          Born Day
        </Link>

        <div className="bd-surface p-7">
          <h1 className="text-3xl font-extrabold mb-2" style={{ letterSpacing: "-0.04em" }}>Create your account.</h1>
          <p className="mb-6" style={{ color: "var(--text-dim)" }}>Start with one group. Add the rest later.</p>

          <button onClick={googleLogin} className="bd-btn bd-btn-ghost w-full mb-4" data-testid="google-register-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.3h6c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2-1.9 3.3-4.7 3.3-8.1z" fill="#4285F4" />
              <path d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2v2.8C3.9 20.5 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.7 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7.1H2c-.8 1.5-1.3 3.1-1.3 4.9s.5 3.4 1.3 4.9l3.7-2.8z" fill="#FBBC05" />
              <path d="M12 5.4c1.6 0 3 .6 4.2 1.7l3.1-3.1C17.5 2.2 15 1 12 1 7.7 1 3.9 3.5 2 7.1l3.7 2.8C6.6 7.4 9.1 5.4 12 5.4z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-4 text-xs" style={{ color: "var(--text-dim)" }}>
            <div className="bd-divider flex-1" /> OR <div className="bd-divider flex-1" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="bd-label">Your name</label>
              <input
                className="bd-input"
                type="text"
                value={name}
                required
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="bd-label">Phone number <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>(optional)</span></label>
              <input
                className="bd-input bd-mono"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                data-testid="register-phone"
                autoComplete="tel"
              />
              <p className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
                Skip if you like. Only used as a private reference if you ever need to recover your account.
              </p>
            </div>
            <div>
              <label className="bd-label">Email</label>
              <input
                className="bd-input"
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="bd-label">Password</label>
              <input
                className="bd-input"
                type="password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password"
                autoComplete="new-password"
              />
            </div>
            {err && (
              <div className="text-sm px-3 py-2 rounded" style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }} data-testid="register-error">
                {err}
              </div>
            )}
            <button className="bd-btn w-full" disabled={busy} data-testid="register-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <>Create account <ArrowRight size={16} /></>}
            </button>
          </form>

          <p className="mt-6 text-sm text-center" style={{ color: "var(--text-dim)" }}>
            Already have one?{" "}
            <Link to="/login" className="font-semibold" style={{ color: "var(--text)" }} data-testid="link-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
